import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Funnel, Car } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CarsPage() {
    const { theme } = useTheme();
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [marqueFilter, setMarqueFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        fetchCars();
    }, [search, marqueFilter, typeFilter]);

    const fetchCars = async () => {
        try {
            setError(null);
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (marqueFilter && marqueFilter !== 'all') params.append('marque', marqueFilter);
            if (typeFilter && typeFilter !== 'all') params.append('type_vehicule', typeFilter);
            const response = await axios.get(`${API}/cars?${params}`);
            setCars(response.data || []);
        } catch (err) {
            console.error('Error fetching cars:', err);
            setError('Erreur lors du chargement des véhicules');
            setCars([]);
        } finally {
            setLoading(false);
        }
    };

    const uniqueMarques = [...new Set(cars.filter(c => c && c.marque && c.marque.trim() !== '').map(c => c.marque))];
    const uniqueTypes = [...new Set(cars.filter(c => c && c.type_vehicule && c.type_vehicule.trim() !== '').map(c => c.type_vehicule))];

    const formatPrice = (price) => {
        if (price === null || price === undefined || isNaN(price)) return 'Prix sur demande';
        try {
            return new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF' }).format(price);
        } catch {
            return `${price} FCFA`;
        }
    };

    const formatKm = (km) => {
        if (km === null || km === undefined || isNaN(km)) return '0';
        try {
            return new Intl.NumberFormat('fr-SN').format(km);
        } catch {
            return String(km);
        }
    };

    return (
        <div data-testid="cars-page" className="min-h-screen">
            {/* Hero Banner */}
            <section className={`py-16 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-mmd-light-bg'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center"
                    >
                        <p className={`text-sm font-medium tracking-[0.2em] uppercase mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                            MMD Luxury Cars
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tighter mb-4">
                            Voitures de Luxe
                        </h1>
                        <p className={`max-w-xl mx-auto ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                            Découvrez notre sélection exclusive de véhicules haut de gamme importés.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Filters */}
            <section className={`py-6 border-b ${theme === 'dark' ? 'bg-mmd-dark-bg border-white/10' : 'bg-white border-black/10'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="relative w-full sm:w-80">
                            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                            <Input
                                placeholder="Rechercher un véhicule..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="car-search"
                            />
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <Funnel size={18} className="text-muted-foreground" />
                            <Select value={marqueFilter} onValueChange={setMarqueFilter}>
                                <SelectTrigger className="w-full sm:w-40" data-testid="car-marque-filter">
                                    <SelectValue placeholder="Marque" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toutes marques</SelectItem>
                                    {uniqueMarques.map(marque => (
                                        <SelectItem key={marque} value={marque}>{marque}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={typeFilter} onValueChange={setTypeFilter}>
                                <SelectTrigger className="w-full sm:w-40" data-testid="car-type-filter">
                                    <SelectValue placeholder="Type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous types</SelectItem>
                                    {uniqueTypes.map(type => (
                                        <SelectItem key={type} value={type}>{type}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </section>

            {/* Cars Grid */}
            <section className={`py-12 ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {error ? (
                        <div className="text-center py-20">
                            <Car size={64} className="mx-auto mb-4 text-red-500" />
                            <h3 className="font-heading text-xl font-medium mb-2 text-red-500">{error}</h3>
                            <button 
                                onClick={fetchCars}
                                className={`mt-4 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-mmd-gold text-mmd-dark-bg' : 'bg-mmd-navy text-white'}`}
                            >
                                Réessayer
                            </button>
                        </div>
                    ) : loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <div key={i} className={`rounded-xl overflow-hidden ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`}>
                                    <div className="aspect-[16/10] bg-muted animate-pulse" />
                                    <div className="p-4 space-y-3">
                                        <div className="h-4 bg-muted rounded animate-pulse w-1/4" />
                                        <div className="h-6 bg-muted rounded animate-pulse w-3/4" />
                                        <div className="h-5 bg-muted rounded animate-pulse w-1/3" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : cars.length === 0 ? (
                        <div className="text-center py-20">
                            <Car size={64} className="mx-auto mb-4 text-muted-foreground" />
                            <h3 className="font-heading text-xl font-medium mb-2">Aucun véhicule trouvé</h3>
                            <p className="text-muted-foreground">Essayez de modifier vos critères de recherche.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {cars.map((car, index) => {
                                if (!car || !car.id) return null;
                                return (
                                    <motion.div
                                        key={car.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: Math.min(index * 0.05, 0.5) }}
                                    >
                                        <Link
                                            to={`/voitures/${car.id}`}
                                            data-testid={`car-card-${car.id}`}
                                            className={`block rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                                                theme === 'dark' ? 'bg-mmd-surface-dark border border-white/10' : 'bg-white border border-black/5'
                                            }`}
                                        >
                                            <div className="aspect-[16/10] overflow-hidden relative">
                                                <img
                                                    src={car.images?.[0] || 'https://via.placeholder.com/640x400?text=No+Image'}
                                                    alt={`${car.marque || ''} ${car.modele || ''}`}
                                                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/640x400?text=Image+Error';
                                                    }}
                                                />
                                                {car.type_vehicule && (
                                                    <span className={`absolute top-3 right-3 px-2 py-1 rounded text-xs font-medium ${
                                                        theme === 'dark' ? 'bg-mmd-gold text-mmd-dark-bg' : 'bg-mmd-navy text-white'
                                                    }`}>
                                                        {car.type_vehicule}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="p-4">
                                                <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                                                    {car.marque || 'Marque'}
                                                </p>
                                                <h3 className="font-heading font-semibold text-lg mb-2">
                                                    {car.modele || 'Modèle'}
                                                </h3>
                                                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                                    <span>{car.annee || 'N/A'}</span>
                                                    <span>{formatKm(car.kilometrage)} km</span>
                                                </div>
                                                <span className={`text-lg font-bold ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                                                    {formatPrice(car.prix)}
                                                </span>
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
